<template>
    <div class="bg-gray-100 min-h-screen py-8">
        <div class="container mx-auto p-4">
            <div class="max-w-4xl mx-auto">
                <div class="bg-white shadow-lg rounded-lg border-0 overflow-hidden">
                    <!-- En-tête du formulaire -->
                    <div class="bg-orange-500 text-white flex justify-between items-center p-6">
                        <h4 class="text-2xl font-semibold mb-0">
                            <i class="fas fa-user-plus mr-3"></i>Modifier mon profil
                        </h4>
                    </div>

                    <!-- Navigation des onglets -->
                    <div class="border-b border-gray-200">
                        <nav class="flex">
                            <button
                                :class="activeTab === 'personal'
                                    ? 'flex-1 py-4 px-6 text-sm font-medium border-b-2 text-orange-500 border-orange-500 bg-orange-50'
                                    : 'flex-1 py-4 px-6 text-sm font-medium border-b-2 border-transparent bg-gray-100 text-gray-600 hover:text-orange-600 transition duration-200'"
                                @click="showTab('personal')"
                            >
                                <i class="fas fa-user mr-2"></i>Informations personnelles
                            </button>
                            <button
                                :class="activeTab === 'security'
                                    ? 'flex-1 py-4 px-6 text-sm font-medium border-b-2 text-orange-500 border-orange-500 bg-orange-50'
                                    : 'flex-1 py-4 px-6 text-sm font-medium border-b-2 border-transparent bg-gray-100 text-gray-600 hover:text-orange-600 transition duration-200'"
                                @click="showTab('security')"
                            >
                                <i class="fas fa-lock mr-2"></i>Sécurité du compte
                            </button>
                            <button
                                :class="activeTab === 'profile'
                                    ? 'flex-1 py-4 px-6 text-sm font-medium border-b-2 text-orange-500 border-orange-500 bg-orange-50'
                                    : 'flex-1 py-4 px-6 text-sm font-medium border-b-2 border-transparent bg-gray-100 text-gray-600 hover:text-orange-600 transition duration-200'"
                                @click="showTab('profile')"
                            >
                                <i class="fas fa-id-card mr-2"></i>Profil & Préférences
                            </button>
                        </nav>
                    </div>

                    <form :method="method" :action="submitUrl" :enctype="enctype">
                        <div class="p-6">
                            <!-- Onglet 1: Informations personnelles -->
                            <div v-show="activeTab === 'personal'" class="space-y-4">
                                <h5 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-6">
                                    <i class="fas fa-user mr-2 text-orange-500"></i>Vos informations personnelles
                                </h5>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <!-- Nom -->
                                    {{ props.user.id }}
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Nom <span class="text-red-500">*</span>
                                        </label>
                                        <input
                                            v-model="formData.nom"
                                            type="text"
                                            name="nom"
                                            :class="errors.nom
                                                ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                                : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                            placeholder="Votre nom"
                                        />
                                        <p v-if="errors.nom" class="text-red-500 text-sm mt-1">{{ errors.nom }}</p>
                                    </div>

                                    <!-- Prénom -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Prénom <span class="text-red-500">*</span>
                                        </label>
                                        <input
                                            v-model="formData.prenom"
                                            type="text"
                                            name="prenom"
                                            :class="errors.prenom
                                                ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                                : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                            placeholder="Votre prénom"
                                        />
                                        <p v-if="errors.prenom" class="text-red-500 text-sm mt-1">{{ errors.prenom }}</p>
                                    </div>
                                </div>

                                <!-- Section -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Section <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="formData.section"
                                        name="section"
                                        :class="errors.section
                                            ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200 bg-white'
                                            : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200 bg-white'"
                                    >
                                        <option value="">Sélectionnez une section</option>
                                        <option value="informatique">Informatique</option>
                                        <option value="mathematiques">Mathématiques</option>
                                        <option value="physique">Physique</option>
                                        <option value="chimie">Chimie</option>
                                    </select>
                                    <p v-if="errors.section" class="text-red-500 text-sm mt-1">{{ errors.section }}</p>
                                </div>

                                <!-- Niveau -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Niveau <span class="text-red-500">*</span>
                                    </label>
                                    <select
                                        v-model="formData.niveau"
                                        name="niveau"
                                        :class="errors.niveau
                                            ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200 bg-white'
                                            : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200 bg-white'"
                                    >
                                        <option value="">Sélectionnez un niveau</option>
                                        <option value="L1">Licence 1</option>
                                        <option value="L2">Licence 2</option>
                                        <option value="L3">Licence 3</option>
                                        <option value="M1">Master 1</option>
                                        <option value="M2">Master 2</option>
                                    </select>
                                    <p v-if="errors.niveau" class="text-red-500 text-sm mt-1">{{ errors.niveau }}</p>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <!-- Email -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Adresse email <span class="text-red-500">*</span>
                                        </label>
                                        <input
                                            v-model="formData.email"
                                            type="email"
                                            name="email"
                                            :class="errors.email
                                                ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                                : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                            placeholder="votre.email@exemple.com"
                                        />
                                        <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
                                    </div>

                                    <!-- Téléphone -->
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 mb-1">
                                            Téléphone
                                        </label>
                                        <input
                                            v-model="formData.telephone"
                                            type="tel"
                                            name="telephone"
                                            class="block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200"
                                            placeholder="+227 XX XX XX XX"
                                        />
                                    </div>
                                </div>

                                <div class="flex justify-end mt-8">
                                    <button
                                        type="button"
                                        @click="showTab('security')"
                                        class="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-md transition duration-200"
                                    >
                                        Suivant <i class="fas fa-arrow-right ml-2"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Onglet 2: Sécurité du compte -->
                            <div v-show="activeTab === 'security'" class="space-y-4">
                                <h5 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-6">
                                    <i class="fas fa-lock mr-2 text-orange-500"></i>Sécurité de votre compte
                                </h5>

                                <!-- Nom d'utilisateur -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Nom d'utilisateur <span class="text-red-500">*</span>
                                    </label>
                                    <input
                                        v-model="formData.username"
                                        type="text"
                                        name="username"
                                        :class="errors.username
                                            ? 'block w-full border border-red-500 rounded-md p-3 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                            : 'block w-full border border-gray-300 rounded-md p-3 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                        placeholder="nom_utilisateur"
                                    />
                                    <small class="text-gray-500">Uniquement lettres, chiffres et tirets bas</small>
                                    <p v-if="errors.username" class="text-red-500 text-sm mt-1">{{ errors.username }}</p>
                                </div>

                                <!-- Mot de passe -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Mot de passe <span class="text-red-500">*</span>
                                    </label>
                                    <div class="relative">
                                        <input
                                            v-model="formData.password"
                                            :type="showPassword ? 'text' : 'password'"
                                            name="password"
                                            :class="errors.password
                                                ? 'block w-full border border-red-500 rounded-md p-3 pr-10 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                                : 'block w-full border border-gray-300 rounded-md p-3 pr-10 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                            placeholder="Minimum 8 caractères"
                                        />
                                        <button
                                            type="button"
                                            @click="togglePassword('password')"
                                            class="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition duration-200"
                                        >
                                            <i :class="`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`"></i>
                                        </button>
                                    </div>
                                    <small class="text-gray-500">Au moins 8 caractères avec majuscules, minuscules et chiffres</small>
                                    <p v-if="errors.password" class="text-red-500 text-sm mt-1">{{ errors.password }}</p>
                                </div>

                                <!-- Confirmation mot de passe -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Confirmer le mot de passe <span class="text-red-500">*</span>
                                    </label>
                                    <div class="relative">
                                        <input
                                            v-model="formData.password_confirmation"
                                            :type="showPasswordConfirmation ? 'text' : 'password'"
                                            name="password_confirmation"
                                            :class="errors.password_confirmation
                                                ? 'block w-full border border-red-500 rounded-md p-3 pr-10 shadow-sm focus:border-red-500 focus:ring focus:ring-red-200 focus:ring-opacity-50 transition duration-200'
                                                : 'block w-full border border-gray-300 rounded-md p-3 pr-10 shadow-sm focus:border-orange-500 focus:ring focus:ring-orange-200 focus:ring-opacity-50 transition duration-200'"
                                            placeholder="Répétez votre mot de passe"
                                        />
                                        <button
                                            type="button"
                                            @click="togglePassword('password_confirmation')"
                                            class="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition duration-200"
                                        >
                                            <i :class="`fas ${showPasswordConfirmation ? 'fa-eye-slash' : 'fa-eye'}`"></i>
                                        </button>
                                    </div>
                                    <p v-if="errors.password_confirmation" class="text-red-500 text-sm mt-1">{{ errors.password_confirmation }}</p>
                                </div>

                                <div class="flex justify-between mt-8">
                                    <button
                                        type="button"
                                        @click="showTab('personal')"
                                        class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-md transition duration-200"
                                    >
                                        <i class="fas fa-arrow-left mr-2"></i>Précédent
                                    </button>
                                    <button
                                        type="button"
                                        @click="showTab('profile')"
                                        class="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-md transition duration-200"
                                    >
                                        Suivant <i class="fas fa-arrow-right ml-2"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Onglet 3: Profil & Préférences -->
                            <div v-show="activeTab === 'profile'" class="space-y-4">
                                <h5 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-6">
                                    <i class="fas fa-id-card mr-2 text-orange-500"></i>Profil et préférences
                                </h5>

                                <!-- Photo de profil -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Photo de profil
                                    </label>
                                    <div class="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-orange-400 transition duration-200">
                                        <input
                                            ref="photoInput"
                                            type="file"
                                            accept="image/*"
                                            @change="handleFileChange"
                                            class="hidden"
                                            id="photo-upload"
                                            name="photo"
                                        />
                                        <label for="photo-upload" class="cursor-pointer block">
                                            <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                                            <p class="text-sm text-gray-600">Cliquez pour télécharger une photo</p>
                                            <p class="text-xs text-gray-500">JPG, PNG, GIF (max. 2MB)</p>
                                        </label>
                                        <p v-if="formData.photo" class="text-sm text-green-600 mt-2">
                                            {{ formData.photo.name }}
                                        </p>
                                    </div>
                                </div>

                                <!-- Composant personnalisé pour fichier document -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-1">
                                        Document numérique
                                    </label>
                                    <div class="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-orange-400 transition duration-200">
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            @change="handleDocumentChange"
                                            class="hidden"
                                            id="document-upload"
                                            name="metadata[book_file]"
                                        />
                                        <label for="document-upload" class="cursor-pointer block">
                                            <i class="fas fa-file-upload text-3xl text-gray-400 mb-2"></i>
                                            <p class="text-sm text-gray-600">Choisir le document numérique</p>
                                            <p class="text-xs text-gray-500">PDF, DOC, DOCX (max. 5MB)</p>
                                        </label>
                                        <p v-if="formData.document" class="text-sm text-green-600 mt-2">
                                            {{ formData.document.name }}
                                        </p>
                                    </div>
                                </div>

                                <div class="flex justify-between mt-8">
                                    <button
                                        type="button"
                                        @click="showTab('security')"
                                        class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-md transition duration-200"
                                    >
                                        <i class="fas fa-arrow-left mr-2"></i>Précédent
                                    </button>
                                    <div class="flex gap-3">
                                        <button
                                            type="button"
                                            @click="resetForm"
                                            class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-md transition duration-200"
                                        >
                                            <i class="fas fa-times mr-2"></i>Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            class="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-md transition duration-200"
                                        >
                                            <i class="fas fa-user-plus mr-2"></i>Enregistrer les modifications
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue'

const props = defineProps({
    submitUrl: {
        type: String,
        default: '#'
    },
    user:Object,
    method: {
        type: String,
        default: 'POST'
    },
    csrf: String,
    enctype: {
        type: String,
        default: 'multipart/form-data'
    },
})

const activeTab = ref('personal')

// État du formulaire
const formData = reactive({
    nom: '',
    prenom: '',
    section: '',
    niveau: '',
    email: '',
    telephone: '',
    username: '',
    password: '',
    password_confirmation: '',
    photo: null,
    document: null
})

// État pour l'affichage des mots de passe
const showPassword = ref(false)
const showPasswordConfirmation = ref(false)

const errors = reactive({})

// Référence pour l'input file
const photoInput = ref(null)

// Navigation entre onglets
const showTab = (tabName) => {
    activeTab.value = tabName
}

// Basculer l'affichage du mot de passe
const togglePassword = (field) => {
    if (field === 'password') {
        showPassword.value = !showPassword.value
    } else if (field === 'password_confirmation') {
        showPasswordConfirmation.value = !showPasswordConfirmation.value
    }
}



// // Soumission du formulaire
// const handleSubmit = (event) => {
//     event.preventDefault()
//     if (validateForm()) {
//         console.log('Données du formulaire:', formData)
//         alert('Formulaire valide ! Enregistrement en cours...')
//         // Ici vous pourriez envoyer les données à votre API
//     } else {
//         // Aller au premier onglet avec des erreurs
//         if (errors.nom || errors.prenom || errors.section || errors.niveau || errors.email) {
//             activeTab.value = 'personal'
//         } else if (errors.username || errors.password || errors.password_confirmation) {
//             activeTab.value = 'security'
//         }
//         alert('Veuillez corriger les erreurs dans le formulaire.')
//     }
// }

// Gestion du fichier photo
const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
        // Vérifier la taille du fichier (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            alert('Le fichier est trop volumineux. Taille maximum : 2MB')
            event.target.value = ''
            return
        }

        // Vérifier le type de fichier
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
        if (!allowedTypes.includes(file.type)) {
            alert('Type de fichier non autorisé. Utilisez JPG, PNG ou GIF.')
            event.target.value = ''
            return
        }

        formData.photo = file
    }
}


// Réinitialiser le formulaire
const resetForm = () => {
    Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'boolean') {
            formData[key] = false
        } else {
            formData[key] = key === 'photo' || key === 'document' ? null : ''
        }
    })

    // Réinitialiser les erreurs
    Object.keys(errors).forEach(key => delete errors[key])

    // Réinitialiser les inputs file
    if (photoInput.value) {
        photoInput.value.value = ''
    }

    const documentInput = document.getElementById('document-upload')
    if (documentInput) {
        documentInput.value = ''
    }

    // Retourner au premier onglet
    activeTab.value = 'personal'

    // Réinitialiser l'affichage des mots de passe
    showPassword.value = false
    showPasswordConfirmation.value = false
}
</script>

<style scoped>
/* Styles personnalisés */
.container {
    max-width: 1200px;
}

/* Animation pour les transitions d'onglets */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}

/* Styles pour les champs avec erreurs */
.border-red-500:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* Styles pour les champs normaux */
.border-gray-300:focus {
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
}

/* Amélioration des boutons d'onglets */
button:focus {
    outline: none;
}

/* Styles pour les zones d'upload */
.border-dashed:hover {
    background-color: rgba(249, 115, 22, 0.05);
}
</style>
